import { computed } from "vue";
import type { Ref, ComputedRef } from "vue";
import { useInfiniteQuery } from "@tanstack/vue-query";
import type { InfiniteData } from "@tanstack/vue-query";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import { reservationRequestKeys } from "../data/queryKeys";
import type { ReservationRequestListFilters } from "../data/queryKeys";
import type { ReservationRequest } from "@lemmonhq/types";

interface PageData {
  data: ReservationRequest[];
  pagination: { limit: number; offset: number; hasNext: boolean };
}

export function useReservationRequestsQuery(params: {
  filters: Ref<ReservationRequestListFilters>;
  enabled?: Ref<boolean>;
}): {
  pages: ComputedRef<ReservationRequest[]>;
  isLoading: Ref<boolean>;
  isFetchingNextPage: Ref<boolean>;
  hasNextPage: Ref<boolean>;
  fetchNextPage: () => void;
  error: Ref<unknown>;
} {
  const trpc = useTrpc();
  const LIMIT = 50;

  const result = useInfiniteQuery<
    PageData,
    unknown,
    InfiniteData<PageData>,
    readonly unknown[],
    number
  >({
    queryKey: computed(() => reservationRequestKeys.list(params.filters.value)),
    enabled: params.enabled,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const offset = typeof pageParam === "number" ? pageParam : 0;
      const filters = params.filters.value;
      return trpc.reservations.listRequests.query({
        status: filters.status as any,
        customer_name: filters.customer_name,
        date_from: filters.date_from,
        date_to: filters.date_to,
        sort: (filters.sort as any) ?? "requested_time",
        order: (filters.order as any) ?? "asc",
        limit: LIMIT,
        offset,
      });
    },
    getNextPageParam: (lastPage: PageData) =>
      lastPage.pagination.hasNext
        ? lastPage.pagination.offset + lastPage.pagination.limit
        : undefined,
  });

  const pages = computed<ReservationRequest[]>(() => {
    if (!result.data.value) return [];
    return result.data.value.pages.flatMap((page) => page.data);
  });

  return {
    pages,
    isLoading: result.isLoading as Ref<boolean>,
    isFetchingNextPage: result.isFetchingNextPage as Ref<boolean>,
    hasNextPage: result.hasNextPage as Ref<boolean>,
    fetchNextPage: () => result.fetchNextPage(),
    error: result.error as Ref<unknown>,
  };
}
